import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
         AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
         AlertDialogTitle } from "../components/ui/alert-dialog";
import { useRealtimeTable } from "../lib/useRealtimeTable";
import { konfirmasiDenganMutasi } from "../lib/konfirmasiMutasi";
import { Wallet } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { jamPesanan, useStoreTimezones } from "../lib/timezone";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, Paginasi } from "../components/etams/PalangDaftar";
import SaklarDokuCabang from "../components/etams/SaklarDokuCabang";

// Konfirmasi Pembayaran — pola daftar seragam mode SERVER (`docs/POLA_DAFTAR.md`), tampilan KARTU.
//
// ⛔ T92 (titipan auditor 1 Nov 2026): dulu layar ini hanya memuat TANDA JADI dan menulis "DP dan Pelunasan
//    dikonfirmasi dari layar FA". Owner mencari FASBY-2610-0001 di sini → "0 cocok", tanpa petunjuk ke mana; KC baru
//    mudah bingung dan bukti DP tertahan (FADetail: 23 FA pernah menumpuk karena hal serupa). Kini SATU daftar memuat
//    SEMUA bukti yang menunggu — tanda jadi, uang muka (DP), pelunasan — per cabang, tiap baris punya tombol ke FA/PD-nya
//    (`GET /payments/konfirmasi/menunggu`, paling lama menunggu di atas). Pencarian yang tidak menemukan bukti menunggu
//    menunjukkan FA/PD bernomor cocok supaya orang tahu ke mana.
//
// Konfirmasi di sini = jalur yang SAMA dengan layar FA (`POST /payments/confirm`: nominal mutasi K221/K227, bukti
// kembar T4/K231, pemohon ≠ penyetuju). Tautan DOKU DP/pelunasan yang belum dibayar tidak dimuat — cek status &
// konfirmasi manualnya di layar FA (T30). Tautan DOKU tanda jadi tetap dimuat seperti sebelumnya.
export const LABEL_JENIS = { TANDA_JADI: "Tanda jadi", DP: "Uang muka (DP)", PELUNASAN: "Pelunasan" };

export function ruteInduk(b) {
  if (!b?.induk_id) return null;
  return b.induk_jenis === "FA" ? `/fa-orders/${b.induk_id}` : `/design-requests/${b.induk_id}`;
}

export default function PaymentsPending() {
  useStoreTimezones();   // T93: jam menurut zona cabang pesanan + label WIB/WITA
  const { isOwner, isKepalaCabang, profile } = useAuth();
  const [baris, setBaris] = useState([]);
  const [total, setTotal] = useState(0);
  const [cabang, setCabang] = useState([]);
  const [perJenis, setPerJenis] = useState({});
  const [indukCocok, setIndukCocok] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectReason, setRejectReason] = useState({});
  const [apiError, setApiError] = useState(null);
  //: Pembayaran yang sedang menunggu konfirmasi AKHIR dari orangnya.
  //: ⚠ Sebelum 27 Agustus 2026, tombol "Approve" LANGSUNG mengeksekusi:
  //: satu klik mengakui uang masuk, nol gerbang, nol undo. Salah klik di
  //: sini bukan Ctrl+Z, ia koreksi akuntansi.
  const [konfirmasiPr, setKonfirmasiPr] = useState(null);

  const daftar = useDaftar(baris, {
    server: { total, urutSah: [] },
    saringan: {
      cabang: { label: "Cabang", param: "store_id", tampil: (v) => cabang.find((c) => c.id === v)?.nama || "Cabang" },
      jenis: { label: "Jenis", param: "jenis", tampil: (v) => LABEL_JENIS[v] || v },
    },
  });
  const { cari, limit, offset } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments/konfirmasi/menunggu", {
        params: { ...(cari && { q: cari }), ...paramSaringan, limit, offset },
      });
      setBaris(r.data?.items || []);
      setTotal(typeof r.data?.total === "number" ? r.data.total : 0);
      setCabang(r.data?.cabang || []);
      setPerJenis(r.data?.per_jenis || {});
      setIndukCocok(r.data?.induk_cocok || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat pembayaran yang menunggu."));
    } finally {
      setLoading(false);
    }
  }, [cari, paramSaringan, limit, offset]);

  // Realtime channel (additive — runs alongside 12s polling until Owner 2-tab verifies).
  useRealtimeTable("payment_records", load);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  async function approve(pr) {
    setActingId(pr.id);
    setApiError(null);
    try {
      // BUSINESS-98 / K221: nominal dari mutasi rekening diketik pemutus. K231: kembar terkonfirmasi → Owner beralasan.
      const r = await konfirmasiDenganMutasi(pr, (LABEL_JENIS[pr.payment_type] || "pembayaran").toLowerCase(), { isOwner });
      if (!r) return;
      toast.success("Pembayaran dikonfirmasi");
      load();
    } catch (e) {
      setApiError({ id: pr.id, detail: { message: apiErrorMessage(e, "Konfirmasi gagal. Coba lagi.") } });
    } finally {
      setActingId(null);
    }
  }

  async function reject(pr) {
    const reason = (rejectReason[pr.id] || "").trim();
    if (!reason) {
      toast.error("Isi alasan tolak dulu.");
      return;
    }
    setActingId(pr.id);
    setApiError(null);
    try {
      await api.post("/payments/reject", { payment_record_id: pr.id, reason });
      toast.success("Pembayaran ditolak");
      load();
    } catch (e) {
      setApiError({ id: pr.id, detail: { message: apiErrorMessage(e, "Penolakan gagal. Coba lagi.") } });
    } finally {
      setActingId(null);
    }
  }

  if (!isOwner && !isKepalaCabang) {
    return (
      <div className="text-sm text-danger" data-testid="payments-pending-forbidden">
        Halaman ini hanya untuk Owner / Kepala Cabang.
      </div>
    );
  }

  const jenisAktif = daftar.nilaiSaringan.jenis || "";
  const cabangAktif = daftar.nilaiSaringan.cabang || "";

  return (
    <div data-testid="payments-pending-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Konfirmasi Pembayaran</h1>
        <Button variant="outline" onClick={load}>Muat ulang</Button>
      </div>
      <div className="text-sm text-muted-foreground mb-4" data-testid="pending-keterangan">
        Semua bukti pembayaran customer yang menunggu konfirmasi — tanda jadi, uang muka (DP), dan pelunasan. Yang paling
        lama menunggu ada di atas.
        {isKepalaCabang && !isOwner && (
          <> Wilayah: <strong>{profile?.region?.name || "—"}</strong>.</>
        )}
        <span className="ml-1">Pembayaran lewat DOKU terkonfirmasi otomatis.</span>
      </div>

      {/* K210 BUSINESS-76: Kepala Cabang boleh MEMATIKAN DOKU untuk cabangnya (menyalakan kembali hanya Owner). */}
      {isKepalaCabang && !isOwner && (
        <div className="mb-4"><SaklarDokuCabang peran="KEPALA_CABANG" /></div>
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari nomor FA / PD, nama, atau telepon customer" testId="pending-palang">
        <div className="flex flex-wrap gap-1" data-testid="pending-saring-jenis">
          {[["", "Semua"], ["TANDA_JADI", LABEL_JENIS.TANDA_JADI], ["DP", LABEL_JENIS.DP], ["PELUNASAN", LABEL_JENIS.PELUNASAN]].map(([nilai, label]) => (
            <Button key={nilai || "semua"} type="button" size="sm" variant={jenisAktif === nilai ? "default" : "outline"}
                    aria-pressed={jenisAktif === nilai}
                    onClick={() => daftar.setSaringan("jenis", nilai)} data-testid={`pending-jenis-${nilai || "semua"}`}>
              {label}{nilai && typeof perJenis[nilai] === "number" ? ` (${perJenis[nilai]})` : ""}
            </Button>
          ))}
        </div>
        {cabang.length > 1 && (
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={cabangAktif}
                  onChange={(e) => daftar.setSaringan("cabang", e.target.value)} data-testid="pending-saring-cabang"
                  aria-label="Saring cabang">
            <option value="">Semua cabang</option>
            {cabang.map((c) => <option key={c.id} value={c.id}>{c.nama} ({c.jumlah})</option>)}
          </select>
        )}
      </PalangDaftar>

      {loading && baris.length === 0 && <div className="text-muted-foreground text-sm">Memuat...</div>}
      {!loading && baris.length === 0 && daftar.cari.trim() && (
        <Card data-testid="pending-tidak-cocok">
          <CardContent className="p-8 text-center text-sm text-muted-foreground space-y-2">
            <div>Tidak ada pembayaran menunggu yang cocok dengan pencarian ini.</div>
            {indukCocok.length > 0 && (
              <div className="space-y-1" data-testid="pending-induk-cocok">
                <div>Nomor yang cocok (tidak ada bukti yang menunggu di sana):</div>
                {indukCocok.map((i) => (
                  <div key={`${i.jenis}-${i.id}`}>
                    <Link className="underline text-foreground" to={i.jenis === "FA" ? `/fa-orders/${i.id}` : `/design-requests/${i.id}`}>
                      Buka {i.jenis} {i.nomor}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {!loading && baris.length === 0 && !daftar.cari.trim() && (
        <Card data-testid="empty-pending">
          <CardContent className="p-12 text-center">
            <Wallet size={32} className="mx-auto text-muted-foreground/40" />
            <div className="text-sm font-medium mt-3">Semua pembayaran sudah dikonfirmasi</div>
            <div className="text-xs text-muted-foreground mt-1">
              Baris baru muncul sendiri dalam 12 detik.
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {baris.map((pr) => {
          const errForRow = apiError?.id === pr.id ? apiError.detail : null;
          const rute = ruteInduk(pr);
          return (
            <Card key={pr.id} data-testid={`pending-${pr.id}`}>
              <CardContent className="pt-6 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <Badge variant="outline" className="mr-2" data-testid={`pending-jenis-label-${pr.id}`}>
                      {LABEL_JENIS[pr.payment_type] || pr.payment_type}
                    </Badge>
                    <span className="font-medium">{formatIDR(pr.amount_idr)}</span>
                    {pr.lewat_doku && <span className="ml-2 text-xs text-muted-foreground">· tautan DOKU</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Diajukan: {jamPesanan(pr.submitted_at, pr.store_id, pr.region_id)}
                  </div>
                </div>
                <div className="text-sm">
                  <div>
                    <strong>{pr.induk_jenis}:</strong>{" "}
                    {rute ? <Link to={rute} className="underline" data-testid={`pending-induk-${pr.id}`}>{pr.induk_nomor}</Link> : pr.induk_nomor}
                    {" "}({pr.customer_name || "—"}{pr.customer_phone ? ` · ${pr.customer_phone}` : ""})
                    {pr.store_name && <span className="text-muted-foreground"> · Cabang {pr.store_name}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Diajukan oleh: {pr.submitter_nama || (pr.submitted_by ? "staf" : "customer lewat Ruang Pesanan")}
                  </div>
                </div>
                {pr.bukti_kembar?.length > 0 && (
                  /* T4: bukti transfer sama persis dengan bukti pembayaran lain — peringatan keras.
                     K231: kembarannya SUDAH dikonfirmasi → DIBLOKIR (Kepala Cabang tanpa tombol; Owner buka beralasan). */
                  <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm text-danger-soft-foreground font-medium"
                       data-testid={`bukti-kembar-${pr.id}`}>
                    {pr.bukti_kembar_diblokir ? pr.kalimat_blokir_kembar : pr.peringatan_bukti_kembar}
                  </div>
                )}
                {pr.proofs?.[0] && (
                  <div className="text-xs text-muted-foreground border rounded p-2 bg-muted/50">
                    <div><strong>Bank pengirim:</strong> {pr.proofs[0].bank_from || "—"}</div>
                    <div><strong>Rekening penerima:</strong> {pr.proofs[0].bank_to || "—"}</div>
                    <div><strong>No. referensi:</strong> {pr.proofs[0].reference_number || "—"}</div>
                    <div><strong>Tanggal transfer:</strong> {pr.proofs[0].transfer_date || "—"}</div>
                    {pr.proofs[0].notes && <div><strong>Catatan:</strong> {pr.proofs[0].notes}</div>}
                  </div>
                )}

                {errForRow && (
                  <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm" data-testid={`row-err-${pr.id}`}>
                    <div className="font-bold text-danger-soft-foreground">Gagal</div>
                    {errForRow.message && (
                      <div className="text-danger-soft-foreground mt-1 whitespace-pre-wrap">{errForRow.message}</div>
                    )}
                  </div>
                )}

                <div className="flex flex-col md:flex-row gap-2">
                  {rute && (
                    <Button asChild variant="outline" data-testid={`pending-buka-${pr.id}`}>
                      <Link to={rute}>Buka {pr.induk_jenis}</Link>
                    </Button>
                  )}
                  {/* K231: kembaran sudah dikonfirmasi → Kepala Cabang tanpa tombol (server juga menolak). */}
                  {(!pr.bukti_kembar_diblokir || isOwner) && (
                    <Button
                      data-testid={`approve-${pr.id}`}
                      disabled={actingId === pr.id}
                      variant={pr.bukti_kembar_diblokir ? "destructive" : "default"}
                      onClick={() => setKonfirmasiPr(pr)}
                    >
                      {actingId === pr.id ? "Memproses..." : pr.bukti_kembar_diblokir ? "Buka blokir & konfirmasi" : "Konfirmasi"}
                    </Button>
                  )}
                  <div className="flex-1 flex gap-2">
                    <Textarea
                      data-testid={`reject-reason-${pr.id}`}
                      placeholder="Alasan tolak (wajib kalau ditolak)"
                      value={rejectReason[pr.id] || ""}
                      onChange={(e) =>
                        setRejectReason((r) => ({ ...r, [pr.id]: e.target.value }))
                      }
                      rows={1}
                      className="flex-1"
                    />
                    <Button
                      data-testid={`reject-${pr.id}`}
                      variant="destructive"
                      disabled={actingId === pr.id}
                      onClick={() => reject(pr)}
                    >
                      Tolak
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Paginasi daftar={daftar} testId="pending-paginasi" />

      {/* ⛔ GERBANG SEBELUM UANG DIAKUI.
          Dialognya SENGAJA menyebut nominal, customer, dan nomor FA/PD dalam
          satu kalimat — bukan "Anda yakin?". Pertanyaan tanpa isi melatih
          orang menekan "Ya" tanpa membaca, dan gerbang yang selalu dijawab
          sama persis sama dengan nol gerbang. */}
      <AlertDialog open={!!konfirmasiPr} onOpenChange={(o) => { if (!o) setKonfirmasiPr(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi pembayaran ini?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>
                  Mengakui {LABEL_JENIS[konfirmasiPr?.payment_type]?.toLowerCase() || "pembayaran"}{" "}
                  <strong className="text-foreground">{formatIDR(konfirmasiPr?.amount_idr)}</strong>
                  {" "}dari <strong className="text-foreground">{konfirmasiPr?.customer_name || "customer tanpa nama"}</strong>
                  {" "}untuk <strong className="text-foreground">{konfirmasiPr?.induk_nomor || "—"}</strong>.
                </div>
                <div className="text-muted-foreground">
                  Diajukan oleh {konfirmasiPr?.submitter_nama || "—"}
                  {konfirmasiPr?.submitted_at ? ` pada ${jamPesanan(konfirmasiPr.submitted_at, konfirmasiPr.store_id, konfirmasiPr.region_id)}` : ""}.
                </div>
                <div className="text-muted-foreground">
                  Pengakuan ini tidak bisa dibatalkan dari layar ini.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="konfirmasi-batal">Batal</AlertDialogCancel>
            <AlertDialogAction
              data-testid="konfirmasi-lanjut"
              onClick={() => { const pr = konfirmasiPr; setKonfirmasiPr(null); approve(pr); }}
            >
              Ya, konfirmasi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
